import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import type { AnySceneObjectT } from "tweenly"
import { selectUndoable } from "../../helpers/selector.helpers"
import { collectObjectIds } from "../../helpers/wrapper.helpers"
import { AppStateT, AppThunkT } from "../store"
import {
    addSelectedAnimationsAction,
    clearSelectedAnimationsAction,
    removeSelectedAnimationsAction,
    setSelectedAnimationsForObjectAction,
} from "./activeAnimation.slice"

type StateT = {
    // value: string | null
    selected: string[]
}

const initialState: StateT = {
    // value: null,
    selected: [],
}

type PayloadT = {
    id: string
}

export const activeObjectSlice = createSlice({
    name: "activeObject",
    initialState,
    reducers: {
        setActiveObjectAction: (state, action: PayloadAction<PayloadT>) => {
            state.selected = [action.payload.id]
        },
        addObjectToSelectedObjectsAction: (state, action: PayloadAction<PayloadT>) => {
            if (
                state.selected.findIndex((s: string) => {
                    return s === action.payload.id
                }) === -1
            ) {
                state.selected.push(action.payload.id)
            }
        },
        setSelectedObjectIdsAction: (state, action: PayloadAction<{ objectIds: string[] }>) => {
            state.selected = action.payload.objectIds
        },
        clearObjectFromSelectedObjectsAction: (state, action: PayloadAction<PayloadT>) => {
            var index: number
            if (
                (index = state.selected.findIndex((s: string) => {
                    return s === action.payload.id
                })) !== -1
            ) {
                state.selected.splice(index, 1)
            }
        },
        clearAllSelectedObjectsAction: (state) => {
            state.selected = []
        },
    },
})

export const {
    setActiveObjectAction,
    addObjectToSelectedObjectsAction,
    setSelectedObjectIdsAction,
    clearObjectFromSelectedObjectsAction,
    clearAllSelectedObjectsAction,
} = activeObjectSlice.actions

/**
 * Add an object to the multiSelection, dropping any selected DESCENDANT of that object
 * (a selected ancestor already represents it).
 */
export const addObjectToSelectedObjectsWithCleanupAction =
    (objectId: string): AppThunkT =>
    (dispatch, getState) => {
        const state = getState()
        const objectsById = selectUndoable(state).objects.value
        const selected = state.activeObject.selected

        const descendantIds = new Set(collectObjectIds(objectId, objectsById))
        descendantIds.delete(objectId)

        selected.forEach((id) => {
            if (descendantIds.has(id)) {
                dispatch(clearObjectFromSelectedObjectsAction({ id }))
                const child = objectsById[id]
                if (child) dispatch(removeSelectedAnimationsAction(Object.keys(child.animations)))
            }
        })

        dispatch(addObjectToSelectedObjectsAction({ id: objectId }))
    }

/**
 * Ids of every selected object plus all of their descendants.
 *
 * Components used to derive this from `useWrapSelectedObject` / `useWrapSelectedObjects`,
 * which also measure the DOM to build a bounding box — work that a plain "is this row
 * highlighted" check does not need. Memoized, so a consumer can subscribe to a boolean and
 * re-render only when its own membership flips, instead of on every selection change.
 */
const selectSelectionSubtreeIds = createSelector(
    [
        (state: AppStateT) => selectUndoable(state).objects.value,
        (state: AppStateT) => state.activeObject.selected,
    ],
    (objectsById, selected) => new Set(selected.flatMap((id) => collectObjectIds(id, objectsById)))
)

const EMPTY_SELECTION: ReadonlySet<string> = new Set()

/**
 * Selected ids as a Set — but only while MORE THAN ONE object is selected.
 *
 * Switching between two single selections keeps returning the very same empty Set, so
 * subscribers do not re-render at all. That is what stops a plain click on the canvas from
 * re-rendering every object on it.
 */
export const selectMultiSelectionIds = createSelector(
    [(state: AppStateT) => state.activeObject.selected],
    (selected): ReadonlySet<string> => (selected.length > 1 ? new Set(selected) : EMPTY_SELECTION)
)

/** True while a multiselection wrapper is active. */
export const selectHasMultiSelection = (state: AppStateT): boolean =>
    state.activeObject.selected.length > 1

export const selectSelectionCount = (state: AppStateT): number => state.activeObject.selected.length

/** True when this exact object is selected (not merely inside a selected parent). */
export const selectIsObjectSelected = (state: AppStateT, objectId: string): boolean =>
    state.activeObject.selected.includes(objectId)

/** True when the object is selected itself or lives inside a selected object. */
export const selectIsObjectInSelection = (state: AppStateT, objectId: string): boolean =>
    state.activeObject.selected.includes(objectId) || selectSelectionSubtreeIds(state).has(objectId)

const setActiveObject = (
    dispatch: (action: unknown) => void,
    id: string,
    animationsIds: string[]
) => {
    dispatch(setActiveObjectAction({ id }))
    dispatch(setSelectedAnimationsForObjectAction({ animationsIds }))
}

/**
 * The selection click, moved out of `useMultiselectObject`.
 *
 * It used to live in that hook, so every CanvasItem, TimelineRow and ObjectsTreeNodeMenu
 * (~700 instances on a large graphic) had to instantiate useObjectActions,
 * useAnimationActions and both wrapper hooks just to have the handler ready — roughly 25
 * store subscriptions and two DOM-measuring computeWrapperBounds calls each, re-evaluated on
 * every dispatched action. As a thunk it reads the very same data from `getState()`, but only
 * when the user actually clicks.
 */
export const multiselectObjectAction =
    (object: AnySceneObjectT, multiSelectKey: boolean, isRootTicker?: boolean): AppThunkT =>
    (dispatch, getState) => {
        const state = getState()
        const objectsById = selectUndoable(state).objects.value
        const selected = state.activeObject.selected

        const animationIds = object.animations ? Object.keys(object.animations) : []

        // the same subtrees the wrapper hooks expose as activeChildObjectIds /
        // activeChildObjectsIds — pure id collection, no DOM measuring needed here
        const singleSelectionSubtree =
            selected.length === 1 ? collectObjectIds(selected[0], objectsById) : []
        const multiSelectionSubtree =
            selected.length > 1 ? selected.flatMap((id) => collectObjectIds(id, objectsById)) : []

        if (!multiSelectKey) {
            // A plain click always collapses the selection down to the clicked object, even
            // when it is a member of an active multiselection.
            //
            // This used to bail out in that case, to stop the native click at the end of a
            // wrapper drag from throwing the multiselection away. That click no longer
            // reaches React at all — it is swallowed in the capture phase, see
            // dragClick.helpers — so the guard was only blocking the legitimate click.
            dispatch(clearSelectedAnimationsAction())
            dispatch(clearAllSelectedObjectsAction())

            if (object.type === "ticker" && object.isLocked && object.parentId) {
                setActiveObject(dispatch, object.parentId, [])
            } else if (object.type === "text" && isRootTicker) {
                const wrapperTicker = object.parentId ? objectsById[object.parentId] : undefined
                if (wrapperTicker?.parentId) {
                    setActiveObject(dispatch, wrapperTicker.parentId, [])
                }
            } else {
                setActiveObject(dispatch, object.id, animationIds)
                dispatch(addSelectedAnimationsAction(animationIds))
                dispatch(addObjectToSelectedObjectsAction({ id: object.id }))
            }
        } else if (!selected.includes(object.id)) {
            //prevent click on child when parent was selected
            if (
                !singleSelectionSubtree.includes(object.id) &&
                !multiSelectionSubtree.includes(object.id)
            ) {
                dispatch(addObjectToSelectedObjectsWithCleanupAction(object.id))
                dispatch(addSelectedAnimationsAction(animationIds))
            }
        } else {
            // shift+click on an already selected object removes it from the selection
            dispatch(clearObjectFromSelectedObjectsAction({ id: object.id }))
            dispatch(removeSelectedAnimationsAction(animationIds))
        }
    }

export default activeObjectSlice.reducer
